import React, { useId, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Box, Collapse, Stack, Typography } from '@mui/material'
import { ArrowLeftIcon } from 'lucide-react'
import { Button } from '@repo/ui'

import { resolveReturnTo, useVeraSpaceStore } from 'stores'
import { useVeraSpaceMutationsQuery } from 'hooks/api'
import { DisclosureTextButton } from 'components/input/DisclosureTextButton'
import { storage } from 'utils'
import { permissionAreas } from 'utils/permissionLabels'
import { spaceGrantHeadline, spaceGrantSummary } from 'utils/veraSupportAccess'

/**
 * VERA-876: Shown when a Vera staffer is operating inside a company's space.
 *
 * Displays the company name and a "Return to Internal" button. Clicking
 * return POSTs the best-effort exit-space audit log, clears the space store,
 * clears selectedCompany from storage, and navigates back to the internal page
 * the staffer came from.
 *
 * VRG-369: that used to be hardcoded to /internal/companies, so entering a demo's
 * space from the Demos console and coming back landed you on a different list.
 * The origin is now recorded at `enter()` time as `returnTo`. `resolveReturnTo`
 * maps it onto an allowlist of internal LIST routes — a stored detail page could
 * point at a record purged while in-space, and landing on a row that no longer
 * exists is worse than landing on the list.
 *
 * The banner is rendered above the main <Outlet /> in Layout.tsx and is
 * invisible when no space session is active.
 *
 * VRG-677: it also says WHAT this brand lets Vera support see, from the grant captured
 * at `enter()` (the same list `useCanAccess` checks in space). Most brands have granted
 * nothing, and before this a staffer met a page of 403s with nothing on screen saying
 * that the cause was the brand's grant, or who could change it. The role it names is
 * "Vera support" — the column's name in the brand's Roles & permissions matrix — not the
 * "Vera Manager role" this banner used to cite, which no brand admin could find.
 *
 * VRG-677 (compact): the full grant sentence wrapped to ~8 lines with the Ask panel open,
 * pushing every page down by a third of the viewport. The banner now shows ONE line
 * (`spaceGrantHeadline`: the scope plus at most two areas, then "+N more") and puts the
 * full sentence, including where the brand changes it, behind "Show details".
 */
export const VeraSpaceBanner = () => {
  const navigate = useNavigate()

  const companyId = useVeraSpaceStore((s) => s.companyId)
  const companyName = useVeraSpaceStore((s) => s.companyName)
  const returnTo = useVeraSpaceStore((s) => s.returnTo)
  const permissions = useVeraSpaceStore((s) => s.permissions)
  const exitSpace = useVeraSpaceStore((s) => s.exit)

  const { exitAsync } = useVeraSpaceMutationsQuery()
  // Hooks before the early return (Rules of Hooks).
  const [detailsOpen, setDetailsOpen] = useState(false)
  const detailsId = useId()

  if (!companyId) return null

  const { areas, viewOnly } = permissionAreas(permissions)
  const grantInput = { areas, viewOnly, grantSize: permissions.length }
  const grantHeadline = spaceGrantHeadline(grantInput)
  const grantSummary = spaceGrantSummary(grantInput)
  const spaceLabel = `You are in ${companyName ? `${companyName}'s` : 'a company'} space as Vera support.`

  // Resolved BEFORE `exitSpace()` clears the store — reading it after would always
  // see null and always fall back to Companies, which is the bug this replaces.
  const destination = resolveReturnTo(returnTo)

  const handleReturn = () => {
    // Fire the best-effort audit while still in-space (X-Vera-Space still set), but
    // do NOT await it — the UI exit must be synchronous and can never hang on the
    // audit request. Awaiting it previously left the banner stuck when the request
    // stalled (store never cleared, no navigation).
    exitAsync({ companyId }).catch(() => {
      // ignore — audit is best-effort
    })
    // `to` is the VRG-677 `leaving` handoff: the guards that bounce internal staff off a
    // brand page render BEFORE this navigate commits, and now bounce to the same place.
    exitSpace({ to: destination })
    storage.removeItem('selectedCompany')
    navigate(destination)
  }

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        px: 4,
        py: 1.5,
        // Fixed zinc dark grey (not a theme token — the palette's grey scale is inverted
        // for dark mode, so grey.900 renders light). A mode banner should read the same
        // in light and dark: a distinct dark bar with light text. zinc-800.
        bgcolor: '#27272A',
        color: '#FFFFFF',
        gap: 2,
        flexShrink: 0,
      }}
    >
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography variant="body2" fontWeight={500} noWrap title={spaceLabel}>
          {spaceLabel}
        </Typography>
        <Stack direction="row" alignItems="center" spacing={3} sx={{ mt: 0.5, minWidth: 0 }}>
          <Typography
            variant="body3"
            noWrap
            data-testid="vera-space-grant-summary"
            sx={{ minWidth: 0, color: 'rgba(255,255,255,0.72)' }}
          >
            {grantHeadline}
          </Typography>
          <Box sx={{ flexShrink: 0 }}>
            <DisclosureTextButton
              tone="inverse"
              expanded={detailsOpen}
              onToggle={() => setDetailsOpen((open) => !open)}
              label={{ expanded: 'Hide details', collapsed: 'Show details' }}
              controls={detailsId}
            />
          </Box>
        </Stack>
        <Collapse in={detailsOpen}>
          <Typography
            id={detailsId}
            variant="body3"
            data-testid="vera-space-grant-details"
            sx={{ display: 'block', mt: 1, maxWidth: 720, color: 'rgba(255,255,255,0.72)', lineHeight: 1.5 }}
          >
            {grantSummary}
          </Typography>
        </Collapse>
      </Box>
      <Button
        variant="secondary"
        onClick={handleReturn}
        startIcon={<ArrowLeftIcon size={14} strokeWidth={1.5} />}
        sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
      >
        Return to Internal
      </Button>
    </Box>
  )
}
